import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { X, Plus, Check, Minus, ArrowRight, Zap, Battery, Scale, Ruler } from 'lucide-react';
import { bikes } from '../data/bikes';
import { useLanguage } from '../i18n';
import { useTheme } from '../context/ThemeContext';

// Maximum bikes that can be compared
const MAX_COMPARE = 3;

// Bike Selection Modal
const BikeSelectionModal = ({ onSelect, onClose, excludeIds = [] }) => {
  const { t } = useLanguage();
  const { isDark } = useTheme();
  const availableBikes = bikes.filter(b => !excludeIds.includes(b.id));
  const [searchTerm, setSearchTerm] = useState('');
  
  const filteredBikes = availableBikes.filter(bike => 
    bike.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    t(`categories.${bike.category}.name`).toLowerCase().includes(searchTerm.toLowerCase())
  );
  
  return (
    <div 
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm"
      onClick={onClose}
    >
      <div 
        className={`rounded-3xl max-w-2xl w-full max-h-[80vh] overflow-hidden border ${
          isDark 
            ? 'bg-[#12121a] border-[#2a2a3a]' 
            : 'bg-white border-gray-200'
        }`}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className={`flex items-center justify-between p-6 border-b ${isDark ? 'border-[#2a2a3a]' : 'border-gray-200'}`}>
          <h3 className={`text-xl font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>{t('compare.selectBike')}</h3>
          <button
            onClick={onClose}
            className={`w-10 h-10 rounded-full flex items-center justify-center transition-colors ${
              isDark 
                ? 'bg-[#1a1a25] text-white/60 hover:text-white hover:bg-[#B5A16A]'
                : 'bg-gray-100 text-gray-500 hover:text-white hover:bg-[#B5A16A]'
            }`}
          >
            <X className="w-5 h-5" />
          </button>
        </div>
        
        {/* Search */}
        <div className={`p-4 border-b ${isDark ? 'border-[#2a2a3a]' : 'border-gray-200'}`}>
          <input
            type="text"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder={t('compare.searchPlaceholder')}
            className={`w-full px-4 py-3 rounded-xl border focus:outline-none ${
              isDark 
                ? 'bg-[#1a1a25] border-[#2a2a3a] text-white placeholder-white/40 focus:border-[#B5A16A]'
                : 'bg-gray-50 border-gray-300 text-gray-900 placeholder-gray-400 focus:border-[#B5A16A]'
            }`}
          />
        </div>
        
        {/* Bike List */}
        <div className="overflow-y-auto max-h-[50vh] p-4">
          <div className="grid grid-cols-2 gap-3">
            {filteredBikes.map(bike => {
              const categoryName = t(`categories.${bike.category}.name`);
              return (
                <button
                  key={bike.id}
                  onClick={() => onSelect(bike)}
                  className={`flex items-center gap-3 p-3 rounded-xl border transition-colors text-left ${
                    isDark 
                      ? 'bg-[#1a1a25] border-[#2a2a3a] hover:border-[#B5A16A]'
                      : 'bg-gray-50 border-gray-200 hover:border-[#B5A16A]'
                  }`}
                >
                  <img 
                    src={bike.images[0]} 
                    alt={bike.name}
                    className={`w-16 h-16 object-contain rounded-lg ${isDark ? 'bg-[#0f0f18]' : 'bg-gray-100'}`}
                  />
                  <div className="flex-1 min-w-0">
                    <p className={`font-medium truncate ${isDark ? 'text-white' : 'text-gray-900'}`}>{bike.name}</p>
                    <p className={`text-xs ${isDark ? 'text-[#B5A16A]' : 'text-[#8a7a52]'}`}>{categoryName}</p>
                    <p className={`text-sm ${isDark ? 'text-white/60' : 'text-gray-500'}`}>€{(bike.pricing.launch || bike.pricing.list).toLocaleString()}</p>
                  </div>
                </button>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
};

// Comparison Table Row
const CompareRow = ({ label, values, highlight = false, unit = '', isDark }) => {
  // Find the best value if numeric
  const numericValues = values.map(v => {
    const match = String(v).match(/(\d+)/);
    return match ? parseInt(match[1]) : null;
  });
  const maxVal = Math.max(...numericValues.filter(v => v !== null));
  
  return (
    <tr className={highlight ? (isDark ? 'bg-[#1a1a25]/50' : 'bg-gray-50') : ''}>
      <td className={`py-3 px-4 font-medium border-b ${isDark ? 'text-white/60 border-[#2a2a3a]' : 'text-gray-500 border-gray-200'}`}>
        {label}
      </td>
      {values.map((value, i) => {
        const isMax = numericValues[i] === maxVal && maxVal > 0;
        return (
          <td 
            key={i} 
            className={`py-3 px-4 text-center border-b ${
              isDark ? 'border-[#2a2a3a]' : 'border-gray-200'
            } ${isMax ? (isDark ? 'text-[#B5A16A]' : 'text-[#8a7a52]') + ' font-semibold' : (isDark ? 'text-white' : 'text-gray-900')}`}
          >
            {value}{unit && ` ${unit}`}
            {isMax && <span className="ml-1 text-xs">✓</span>}
          </td>
        );
      })}
      {/* Empty cells for unfilled slots */}
      {Array(MAX_COMPARE - values.length).fill(null).map((_, i) => (
        <td key={`empty-${i}`} className={`py-3 px-4 border-b ${isDark ? 'border-[#2a2a3a]' : 'border-gray-200'}`}></td>
      ))}
    </tr>
  );
};

// Feature Comparison Row
const FeatureCompareRow = ({ feature, bikesData, isDark }) => {
  return (
    <tr>
      <td className={`py-2 px-4 text-sm border-b ${isDark ? 'text-white/60 border-[#2a2a3a]' : 'text-gray-500 border-gray-200'}`}>
        {feature}
      </td>
      {bikesData.map((bike, i) => {
        const bikeFeatures = bike?.features || [];
        const hasFeature = bikeFeatures.some(f => 
          f.toLowerCase().includes(feature.toLowerCase()) ||
          feature.toLowerCase().includes(f.toLowerCase().split(' ')[0])
        );
        return (
          <td key={i} className={`py-2 px-4 text-center border-b ${isDark ? 'border-[#2a2a3a]' : 'border-gray-200'}`}>
            {hasFeature ? (
              <Check className="w-5 h-5 text-green-500 mx-auto" />
            ) : (
              <Minus className={`w-5 h-5 mx-auto ${isDark ? 'text-white/20' : 'text-gray-300'}`} />
            )}
          </td>
        );
      })}
      {Array(MAX_COMPARE - bikesData.length).fill(null).map((_, i) => (
        <td key={`empty-${i}`} className={`py-2 px-4 border-b ${isDark ? 'border-[#2a2a3a]' : 'border-gray-200'}`}></td>
      ))}
    </tr>
  );
};

// Main Comparison Modal
export const ComparisonModal = ({ selectedBikes, onClose, onRemove, onAdd }) => {
  const { t, language } = useLanguage();
  const { isDark } = useTheme();
  const [showAddModal, setShowAddModal] = useState(false);
  
  // Get motor power number
  const getMotorNm = (specs) => {
    const match = specs.motor.match(/(\d+)\s*Nm/);
    return match ? `${match[1]} Nm` : specs.motor;
  };
  
  // Get battery capacity
  const getBatteryWh = (specs) => {
    const match = specs.battery.match(/(\d+)\s*Wh/);
    return match ? `${match[1]} Wh` : specs.battery;
  };
  
  // Get weight number
  const getWeight = (specs) => {
    const match = specs.weight.match(/([\d.]+)/);
    return match ? parseFloat(match[1]) : specs.weight;
  };
  
  // All unique features from selected bikes
  const allFeatures = [...new Set(selectedBikes.flatMap(b => b.features))];
  
  return (
    <div 
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/90 backdrop-blur-sm"
      onClick={onClose}
    >
      <div 
        className={`rounded-3xl max-w-6xl w-full max-h-[90vh] overflow-hidden border ${
          isDark ? 'bg-[#0f0f18] border-[#2a2a3a]' : 'bg-gray-50 border-gray-200'
        }`}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className={`flex items-center justify-between p-6 border-b ${
          isDark ? 'border-[#2a2a3a] bg-[#12121a]' : 'border-gray-200 bg-white'
        }`}>
          <h2 className={`text-2xl font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>{t('compare.title')}</h2>
          <button
            onClick={onClose}
            className={`w-10 h-10 rounded-full flex items-center justify-center transition-colors ${
              isDark 
                ? 'bg-[#1a1a25] text-white/60 hover:text-white hover:bg-red-500'
                : 'bg-gray-100 text-gray-500 hover:text-white hover:bg-red-500'
            }`}
          >
            <X className="w-5 h-5" />
          </button>
        </div>
        
        {/* Content */}
        <div className="overflow-x-auto overflow-y-auto max-h-[calc(90vh-80px)]">
          <table className="w-full min-w-[800px]">
            {/* Bike Headers */}
            <thead>
              <tr>
                <th className={`w-1/5 p-4 text-left font-medium sticky top-0 ${
                  isDark ? 'text-white/60 bg-[#12121a]' : 'text-gray-500 bg-white'
                }`}></th>
                {selectedBikes.map((bike, i) => (
                  <th key={bike.id} className={`w-1/5 p-4 sticky top-0 ${isDark ? 'bg-[#12121a]' : 'bg-white'}`}>
                    <div className="relative group">
                      <button
                        onClick={() => onRemove(bike.id)}
                        className="absolute -top-1 -right-1 w-6 h-6 rounded-full bg-red-500 text-white flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity z-10"
                      >
                        <X className="w-4 h-4" />
                      </button>
                      <div className={`aspect-square rounded-xl overflow-hidden mb-3 ${
                        isDark ? 'bg-gradient-to-br from-[#1a1a25] to-[#0f0f18]' : 'bg-gray-100'
                      }`}>
                        <img 
                          src={bike.images[0]} 
                          alt={bike.name}
                          className="w-full h-full object-contain p-2"
                        />
                      </div>
                      <p className={`font-bold text-lg ${isDark ? 'text-white' : 'text-gray-900'}`}>{bike.name}</p>
                      <p className={`text-sm mb-2 ${isDark ? 'text-[#B5A16A]' : 'text-[#8a7a52]'}`}>{t(`categories.${bike.category}.name`)}</p>
                      <p className={`text-2xl font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>
                        €{(bike.pricing.launch || bike.pricing.list).toLocaleString()}
                      </p>
                      {bike.pricing.launch && (
                        <p className={`text-sm line-through ${isDark ? 'text-white/50' : 'text-gray-400'}`}>
                          €{bike.pricing.list.toLocaleString()}
                        </p>
                      )}
                      <Link
                        to={`/bikes/${bike.slug}`}
                        onClick={onClose}
                        className={`mt-3 inline-flex items-center gap-1 text-sm transition-colors ${
                          isDark ? 'text-[#B5A16A] hover:text-[#c4b17a]' : 'text-[#8a7a52] hover:text-[#6b5f3f]'
                        }`}
                      >
                        {t('compare.viewDetails')}
                        <ArrowRight className="w-4 h-4" />
                      </Link>
                    </div>
                  </th>
                ))}
                {/* Add bike slot */}
                {selectedBikes.length < MAX_COMPARE && (
                  <th className={`w-1/5 p-4 sticky top-0 ${isDark ? 'bg-[#12121a]' : 'bg-white'}`}>
                    <button
                      onClick={() => setShowAddModal(true)}
                      className={`w-full aspect-square rounded-xl border-2 border-dashed flex flex-col items-center justify-center gap-3 transition-colors group ${
                        isDark 
                          ? 'border-[#2a2a3a] hover:border-[#B5A16A]'
                          : 'border-gray-300 hover:border-[#B5A16A]'
                      }`}
                    >
                      <div className={`w-12 h-12 rounded-full flex items-center justify-center group-hover:bg-[#3b82f6] transition-colors ${
                        isDark ? 'bg-[#1a1a25]' : 'bg-gray-100'
                      }`}>
                        <Plus className={`w-6 h-6 group-hover:text-white ${isDark ? 'text-white/60' : 'text-gray-400'}`} />
                      </div>
                      <span className={`text-sm group-hover:text-[#B5A16A] ${isDark ? 'text-white/60' : 'text-gray-500'}`}>
                        {t('compare.addBike')}
                      </span>
                    </button>
                  </th>
                )}
                {/* Empty slots */}
                {Array(MAX_COMPARE - selectedBikes.length - 1).fill(null).map((_, i) => (
                  <th key={`empty-header-${i}`} className={`w-1/5 p-4 sticky top-0 ${isDark ? 'bg-[#12121a]' : 'bg-white'}`}></th>
                ))}
              </tr>
            </thead>
            
            <tbody>
              {/* Key Specs Section */}
              <tr>
                <td colSpan={MAX_COMPARE + 1} className={`py-4 px-4 ${isDark ? 'bg-[#1a1a25]' : 'bg-gray-100'}`}>
                  <h3 className={`font-bold flex items-center gap-2 ${isDark ? 'text-white' : 'text-gray-900'}`}>
                    <Zap className={`w-5 h-5 ${isDark ? 'text-[#B5A16A]' : 'text-[#8a7a52]'}`} />
                    {t('compare.keySpecs')}
                  </h3>
                </td>
              </tr>
              
              <CompareRow 
                label={t('specs.motor')}
                values={selectedBikes.map(b => getMotorNm(b.specs))}
                highlight
                isDark={isDark}
              />
              <CompareRow 
                label={t('specs.battery')}
                values={selectedBikes.map(b => getBatteryWh(b.specs))}
                isDark={isDark}
              />
              <CompareRow 
                label={t('specs.wheels')}
                values={selectedBikes.map(b => b.specs.wheels)}
                highlight
                isDark={isDark}
              />
              <CompareRow 
                label={t('specs.brakes')}
                values={selectedBikes.map(b => b.specs.brakes)}
                isDark={isDark}
              />
              <CompareRow 
                label={t('specs.gears')}
                values={selectedBikes.map(b => b.specs.gears)}
                highlight
                isDark={isDark}
              />
              <CompareRow 
                label={t('specs.fork')}
                values={selectedBikes.map(b => b.specs.fork)}
                isDark={isDark}
              />
              
              {/* Dimensions Section */}
              <tr>
                <td colSpan={MAX_COMPARE + 1} className={`py-4 px-4 ${isDark ? 'bg-[#1a1a25]' : 'bg-gray-100'}`}>
                  <h3 className={`font-bold flex items-center gap-2 ${isDark ? 'text-white' : 'text-gray-900'}`}>
                    <Ruler className={`w-5 h-5 ${isDark ? 'text-[#B5A16A]' : 'text-[#8a7a52]'}`} />
                    {t('compare.dimensions')}
                  </h3>
                </td>
              </tr>
              
              <CompareRow 
                label={t('specs.weight')}
                values={selectedBikes.map(b => `${getWeight(b.specs)} kg`)}
                highlight
                isDark={isDark}
              />
              <CompareRow 
                label={t('specs.dimensions')}
                values={selectedBikes.map(b => b.specs.dimensions)}
                isDark={isDark}
              />
              
              {/* Features Section */}
              <tr>
                <td colSpan={MAX_COMPARE + 1} className={`py-4 px-4 ${isDark ? 'bg-[#1a1a25]' : 'bg-gray-100'}`}>
                  <h3 className={`font-bold flex items-center gap-2 ${isDark ? 'text-white' : 'text-gray-900'}`}>
                    <Check className={`w-5 h-5 ${isDark ? 'text-[#B5A16A]' : 'text-[#8a7a52]'}`} />
                    {t('compare.features')}
                  </h3>
                </td>
              </tr>
              
              {allFeatures.slice(0, 8).map((feature, i) => (
                <FeatureCompareRow 
                  key={i} 
                  feature={feature} 
                  bikesData={selectedBikes}
                  isDark={isDark}
                />
              ))}
            </tbody>
          </table>
        </div>
      </div>
      
      {/* Add Bike Modal */}
      {showAddModal && (
        <BikeSelectionModal
          onSelect={(bike) => {
            onAdd(bike);
            setShowAddModal(false);
          }}
          onClose={() => setShowAddModal(false)}
          excludeIds={selectedBikes.map(b => b.id)}
        />
      )}
    </div>
  );
};

// Floating Compare Bar (shows when bikes are selected)
export const CompareBar = ({ selectedBikes, onRemove, onCompare, onClear }) => {
  const { t } = useLanguage();
  const { isDark } = useTheme();
  
  if (selectedBikes.length === 0) return null;
  
  return (
    <div className={`fixed bottom-0 left-0 right-0 z-40 border-t ${
      isDark 
        ? 'bg-[#12121a] border-[#2a2a3a] shadow-[0_-4px_30px_rgba(0,0,0,0.5)]'
        : 'bg-white border-gray-200 shadow-[0_-4px_30px_rgba(0,0,0,0.1)]'
    }`}>
      <div className="container mx-auto px-4 py-4">
        <div className="flex items-center justify-between gap-4">
          {/* Selected bikes preview */}
          <div className="flex items-center gap-3 overflow-x-auto">
            <span className={`text-sm whitespace-nowrap ${isDark ? 'text-white/60' : 'text-gray-500'}`}>
              {t('compare.selected')} ({selectedBikes.length}/{MAX_COMPARE}):
            </span>
            {selectedBikes.map(bike => (
              <div 
                key={bike.id}
                className={`flex items-center gap-2 px-3 py-2 rounded-xl border ${
                  isDark 
                    ? 'bg-[#1a1a25] border-[#2a2a3a]'
                    : 'bg-gray-50 border-gray-200'
                }`}
              >
                <img 
                  src={bike.images[0]} 
                  alt={bike.name}
                  className="w-8 h-8 object-contain rounded"
                />
                <span className={`text-sm font-medium ${isDark ? 'text-white' : 'text-gray-900'}`}>{bike.name}</span>
                <button
                  onClick={() => onRemove(bike.id)}
                  className="w-5 h-5 rounded-full bg-red-500/20 text-red-400 flex items-center justify-center hover:bg-red-500 hover:text-white transition-colors"
                >
                  <X className="w-3 h-3" />
                </button>
              </div>
            ))}
          </div>
          
          {/* Actions */}
          <div className="flex items-center gap-3">
            <button
              onClick={onClear}
              className={`px-4 py-2 transition-colors text-sm ${
                isDark ? 'text-white/60 hover:text-white' : 'text-gray-500 hover:text-gray-900'
              }`}
            >
              {t('compare.clear')}
            </button>
            <button
              onClick={onCompare}
              disabled={selectedBikes.length < 2}
              className="px-6 py-2 bg-gradient-to-r from-[#B5A16A] to-[#c4b17a] text-white font-semibold rounded-xl disabled:opacity-50 disabled:cursor-not-allowed hover:shadow-[0_0_20px_rgba(181,161,106,0.4)] transition-all"
            >
              {t('compare.compareNow')} ({selectedBikes.length})
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// Compare Toggle Button for BikeCard
export const CompareToggle = ({ bike, isSelected, onToggle }) => {
  const { t } = useLanguage();
  const { isDark } = useTheme();
  
  return (
    <button
      onClick={(e) => {
        e.preventDefault();
        e.stopPropagation();
        onToggle(bike);
      }}
      className={`absolute top-4 right-4 z-10 px-3 py-1.5 rounded-full text-xs font-medium transition-all ${
        isSelected 
          ? 'bg-[#B5A16A] text-white' 
          : isDark
            ? 'bg-[#12121a]/80 border border-[#2a2a3a] text-white/60 hover:text-white hover:border-[#B5A16A]'
            : 'bg-white/90 border border-gray-300 text-gray-500 hover:text-[#8a7a52] hover:border-[#B5A16A]'
      }`}
      data-testid={`compare-toggle-${bike.slug}`}
    >
      {isSelected ? (
        <>
          <Check className="w-3 h-3 inline mr-1" />
          {t('compare.added')}
        </>
      ) : (
        <>
          <Plus className="w-3 h-3 inline mr-1" />
          {t('compare.add')}
        </>
      )}
    </button>
  );
};

export default {
  ComparisonModal,
  CompareBar,
  CompareToggle
};
